/**
 * MKS-533 — "+N guests" on a team event that is open to externals.
 *
 * Shown only to someone whose RSVP is "going" on an event with
 * `allowExternalGuests`. Changing the number updates the RSVP without touching
 * its status, so the control commits on every step rather than behind a Save.
 */
import { MAX_EVENT_GUESTS } from '../../services/api/teamEvents';

interface GuestCountStepperProps {
  value: number;
  onChange: (next: number) => void;
  /**
   * The most guests this person may still claim: MKS-540's per-event limit and
   * the seats the event has left, whichever is smaller. Shown as "up to +N" so
   * nobody has to discover the ceiling by hitting it.
   */
  max?: number;
  disabled?: boolean;
}

export function clampGuestCount(next: number, max = MAX_EVENT_GUESTS): number {
  if (!Number.isFinite(next)) return 0;
  return Math.min(Math.max(Math.trunc(next), 0), Math.max(Math.min(max, MAX_EVENT_GUESTS), 0));
}

export function GuestCountStepper({ value, onChange, max = MAX_EVENT_GUESTS, disabled = false }: GuestCountStepperProps) {
  const upperBound = Math.max(Math.min(max, MAX_EVENT_GUESTS), 0);
  const step = (delta: number) => onChange(clampGuestCount(value + delta, upperBound));
  const buttonClass =
    'px-2.5 py-1 text-base leading-none text-gray-600 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-not-allowed';

  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-600">Bringing guests</span>
      <div className="inline-flex items-center rounded-full border border-gray-300 bg-white overflow-hidden">
        <button
          type="button"
          aria-label="One guest fewer"
          onClick={() => step(-1)}
          disabled={disabled || value <= 0}
          className={buttonClass}
        >
          &minus;
        </button>
        <input
          type="number"
          aria-label="Number of guests"
          min={0}
          max={upperBound}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(clampGuestCount(Number(e.target.value), upperBound))}
          className="w-12 border-x border-gray-200 py-1 text-center text-sm text-gray-900 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
        />
        <button
          type="button"
          aria-label="One guest more"
          onClick={() => step(1)}
          disabled={disabled || value >= upperBound}
          className={buttonClass}
        >
          +
        </button>
      </div>
      <span className="text-sm font-medium text-gray-700">{value === 0 ? 'Just me' : `+${value}`}</span>
      <span className="text-sm text-gray-500">up to +{upperBound}</span>
    </div>
  );
}
